<script lang="ts" setup>
import { computed, ref } from 'vue';
import { BAlert, BButton, BModal, BSpinner } from 'bootstrap-vue-next';
import { useMutation } from '@vue/apollo-composable';
import { InviteResendDocument, InviteType } from '@/gql/graphql';

// --- Props for the component ---
interface Props {
  invite: InviteType;
  show: boolean;
}
const props = defineProps<Props>();
const emits = defineEmits(['success', 'close']);

// --- Computed Properties ---
const show = computed<boolean>(() => {
  return props.show;
});

// --- Mutation: Resend Invite ---
const mutationShowSuccess = ref(false);
const mutationInProgress = ref(false);
const mutationError = ref<string | null>(null);
const {
  mutate: inviteMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(InviteResendDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.inviteResend) {
    console.debug('Invite resent:', res.data.inviteResend);
    // emit event so the parent can refresh the memberships
    mutationShowSuccess.value = true;
    emits('success');
    // close the modal with delay
    setTimeout(() => {
      handleCloseModal();
    }, 2000);
  } else {
    console.error('Invite resend failed:', res.data);
    mutationError.value = `Invite resend failed: ${res.data}`;
  }
});

onMutationError((err) => {
  console.error('Error resending invite:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers ---
function resendInvite() {
  mutationError.value = null;
  mutationInProgress.value = true;

  if (!props.invite || !props.invite.id) {
    mutationInProgress.value = false;
    mutationError.value = 'Invalid invite data.';
    return;
  }

  inviteMutation({
    id: props.invite.id,
  });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <b-modal
    v-if="props.invite.organization"
    v-model="show"
    @hide="handleCloseModal"
  >
    <!-- Title -->
    <template #title>
      <font-awesome-icon :icon="['fas', 'envelope']" class="me-2" />
      <strong>Resend Invite</strong>
    </template>
    <!-- Content -->
    <div>
      <p>
        Confirm to resend an invite to join
        <strong>{{ props.invite.organization.name }}</strong> organization.
      </p>

      <!-- Mutation Error Messages -->
      <BAlert v-model="mutationError" variant="danger" dismissible class="mt-3">
        {{ mutationError }}
      </BAlert>
    </div>

    <!-- Footer with action buttons -->
    <template #footer>
      <BButton variant="secondary" @click="handleCloseModal" class="me-2">
        <span>Cancel</span>
      </BButton>
      <BButton
        variant="primary"
        :disabled="mutationInProgress || mutationShowSuccess"
        @click="resendInvite()"
      >
        <span v-if="mutationInProgress">
          <b-spinner small label="" class="me-1"></b-spinner>
          Resending invite
        </span>
        <span v-else-if="mutationShowSuccess">
          <font-awesome-icon :icon="['fas', 'check-double']" class="me-1" />
          Invite resent
        </span>
        <span v-else>
          <font-awesome-icon :icon="['fas', 'envelope']" class="me-1" />
          Resend invite
        </span>
      </BButton>
    </template>
  </b-modal>
</template>
